import { TICK_MS } from "../movement/step.ts";

/**
 * Drenched from the weather (ADR-144's amendment of 2026-11-01, decisions 3, 4 and 7): how long in rain drenches a
 * character, how fast it dries where it stands, and how wet its drip looks. Pure rules shared by the map server, which
 * decides, and the page, which draws the drip. Integer counts, no allocation.
 */

/**
 * Seconds of rain that drench a character; a storm counts each second twice.
 */
export const DRENCH_AFTER_SECONDS = 90;

/**
 * The longest a Drenched character in rain waits between two refreshes, in seconds, so its owner frame goes at most this
 * often while it rains.
 */
export const DRENCH_REFRESH_SECONDS = 30;

/**
 * Ticks between two steps of one character's exposure: once a second, a twentieth of the characters each tick.
 */
export const DRENCH_STEP_TICKS = 1000 / TICK_MS;

/**
 * How many times faster than in the open a Drenched character dries: sheltered (an indoor map or under a roof) and by a
 * lit fire.
 */
export const DRY_RATE = Object.freeze({ open: 1, sheltered: 2, fire: 4 } as const);

/**
 * How near a lit fire dries a character at the fire's rate, in tiles.
 */
export const FIRE_DRY_RADIUS_TILES = 3;

const MAX_EXPOSURE = 0xffff;

/**
 * A character's exposure after one second: rain (a strength of 1 up to 2) adds 1, a storm (2) adds 2, and a dry second,
 * or one sheltered, sets it to 0.
 *
 * @param count The exposure before, in seconds of rain.
 * @param strength The region's weather strength now (`weatherStrengthAt`), 0 to 2.
 * @param sheltered Whether it stands indoors or under a roof.
 * @returns The exposure after, at most 65,535.
 */
export function exposureAfter(count: number, strength: number, sheltered: boolean): number {
  if (sheltered || strength < 1) {
    return 0;
  }
  return Math.min(MAX_EXPOSURE, count + (strength >= 2 ? 2 : 1));
}

/**
 * Whether an exposure drenches.
 *
 * @param count The exposure, in seconds of rain.
 * @returns True at `DRENCH_AFTER_SECONDS` or more.
 */
export function drenchedBy(count: number): boolean {
  return count >= DRENCH_AFTER_SECONDS;
}

/**
 * How fast a Drenched character dries where it stands, a lit fire winning over shelter.
 *
 * @param sheltered Whether it stands indoors or under a roof.
 * @param nearFire Whether a lit fire is within `FIRE_DRY_RADIUS_TILES`.
 * @returns The rate, a whole number from `DRY_RATE`.
 */
export function dryRate(sheltered: boolean, nearFire: boolean): number {
  return nearFire ? DRY_RATE.fire : sheltered ? DRY_RATE.sheltered : DRY_RATE.open;
}

/**
 * The ticks left after a change of drying rate, keeping the drying still to do: what was left at the old rate times the
 * old rate over the new, rounded up.
 *
 * @param left The ticks left at the old rate.
 * @param from The old rate.
 * @param to The new rate.
 * @returns The ticks left at the new rate.
 * @example
 * rescaledTicks(3000, 1, 2);
 * // 1500: stepping indoors halves the time left
 */
export function rescaledTicks(left: number, from: number, to: number): number {
  return Math.ceil((left * from) / to);
}

/**
 * How wet a Drenched character's drip looks: its time left over the effect's duration.
 *
 * @param ticksLeft The ticks left.
 * @param durationTicks The effect's duration.
 * @returns 0 dry to 1 just drenched.
 */
export function dripLevel(ticksLeft: number, durationTicks: number): number {
  if (durationTicks <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(1, ticksLeft / durationTicks));
}
